import defer from "lodash/defer"
import get from "lodash/get"
import find from "lodash/find"
import { useEffect, useState, forwardRef } from "react"
import { ReactSVG } from "react-svg"
import InputAdornment from "component-lib/Base/components/InputAdornment"
import TextField from "component-lib/Base/components/TextField"
import { makeStyles } from "tss-react/mui"
import { Configure, Index, useSearchBox } from "react-instantsearch"
import enhanceWithClickOutside from "react-click-outside"
import getContentUrl from "utils-lib/getContentUrl"
import getLanguageRoute from "utils-lib/getLanguageRoute"
import { withLocalization } from "hoc-lib/localization"
import { KEY_CODE_MAPPING } from "constants-lib/keyCodes"
import { getWindow } from "utils-lib/getWindow"
import { isTeamsitePage } from "utils-lib/isTeamsitePage"
import { isMyWMApp } from "utils-lib/isMyWMApp"
import { screenReaderSpeak } from "utils-lib/accessibility/screenReader"
import { Link } from "../../Base"
import { useAutocomplete } from "../utils/useAutocomplete"
import Queries from "../components/Queries/Queries"
import Results from "../components/Results/Results"
import Suggestions from "../components/Suggestions/Suggestions"
import { pushSearchSubmitData } from "./analytics/analytics"

let timer
const GLOBAL_SEARCH_QUERY_SUBMIT_INPUT = "global-search-query-submit-input"
export const strings = {
  en_CA: {
    RESULTS: `Results`,
  },
  fr_CA: {
    RESULTS: `Résultats`,
  },
}

const useStyles = makeStyles()((theme) => ({
  inputContainer: {
    maxWidth: 828,
    width: `100%`,
    [theme.breakpoints.down(`lg`)]: {
      maxWidth: 660,
    },
    "&::before": {
      content: '""',
      display: "block",
      background: `#fff`,
      height: 4,
      width: `calc(100% - 2px)`,
      position: `absolute`,
      left: 0,
      bottom: 0,
      zIndex: 100,
      marginLeft: 1,
    },
  },
  navInputText: {
    border: `1px solid #EFEFEF`,
    boxShadow: `0 1px 4px 0 rgba(0,0,0,0.2)`,
    borderBottom: 0,
    borderRadius: `10px 10px 0 0`,
    margin: 0,
    marginTop: `.5rem`,
  },
  svg: {
    "& > svg": {
      height: 25,
      width: 25,
    },
    "& ellipse, line": {
      stroke: `#1D890A`,
    },
  },
  allResults: {
    border: `1px solid #D2E7CE !important`,
    borderRadius: `5px !important`,
    "&:hover": {
      border: `1px solid #005D02 !important`,
    },
  },
  inputUnderline: {
    background: `#1D890A`,
    height: 2,
    top: -4,
    zIndex: 101,
  },
  dropdown: {
    left: 0,
    maxHeight: 1000,
    top: 57,
    overflowY: `auto`,
    borderRadius: `0 0 10px 10px`,
    border: `1px solid #EFEFEF`,
    boxShadow: `0 1px 4px 0 rgba(0,0,0,0.2)`,
    transition: `.3s all`,
    zIndex: 100,
  },
}))

export const GlobalDesktopSearch = forwardRef((props, ref) => {
  const {
    error = null,
    errorMessageDescription,
    errorMessageTitle,
    failureDescription,
    failureTitle,
    language,
    numCharacters = 3,
    numHighlightedResults,
    numResults = 6,
    numberQueries,
    onClose,
    queryIndex,
    indices = [],
    refine = () => {},
    resultsText,
    search,
    searchBarPlaceholderText,
    seeAllResultsText,
    setSearch,
    strings,
    suggestedText,
    resultsIndex,
    suggestionIndex,
    searchDelay,
    filterType,
  } = props
  const [showDropdown, setShowDropdown] = useState(false)
  const [isFocused, setIsFocused] = useState(false)

  const ssrWindow = getWindow()

  const allSearchResults = get(
    find(indices, ({ indexId }) => `${resultsIndex}-results` === indexId),
    `results`,
  )

  const totalResults = allSearchResults ? get(allSearchResults, `nbHits`, 0) : 0

  useEffect(() => {
    if (!showDropdown && totalResults) {
      defer(() => {
        setShowDropdown(true)
      })
    }
  }, [totalResults])

  useEffect(() => {
    const input = ssrWindow.document.getElementById(
      `full-width-text-field-header`,
    )

    if (input) {
      input.focus()
    }
  }, [])

  const { classes } = useStyles()

  const languageRoute = getLanguageRoute(language)

  const suggestionsResults = allSearchResults && allSearchResults[resultsIndex]
  let suggestionsCount = 0

  if (suggestionsResults) {
    suggestionsCount = suggestionsResults.hits.length
  }

  function renderSuggested(hideDescription) {
    return (
      <Index
        indexName={suggestionIndex}
        indexId={`${suggestionIndex}-suggested`}
      >
        <Configure
          analyticsTags={["desktop-global-suggested"]}
          hitsPerPage={numHighlightedResults}
          filters={`tags:'${filterType}'`}
          clickAnalytics
        />
        <Suggestions
          className="SiteSearch__result-desktop"
          indexName={`${suggestionIndex}-suggested`}
          hideDescription={hideDescription}
        />
      </Index>
    )
  }

  function renderResults() {
    return (
      <Index indexName={resultsIndex} indexId={`${resultsIndex}-results`}>
        <Configure
          analyticsTags={["desktop-global-results"]}
          hitsPerPage={numResults}
          clickAnalytics
        />
        <Results
          className="SiteSearch__result-desktop"
          hideDescription
          indexName={resultsIndex}
          numberToRender={numResults}
        />
      </Index>
    )
  }

  const isPreSearch = search.length < numCharacters
  const isExternalLink =
    isMyWMApp || isTeamsitePage(ssrWindow.location.pathname)

  useEffect(() => {
    screenReaderSpeak(
      isPreSearch
        ? suggestedText
        : `${totalResults} ${resultsText || strings.RESULTS}`,
      "assertive",
    )
  }, [isPreSearch, totalResults, resultsText, strings.RESULTS, search])

  return (
    <div
      className="CustomAutoCompleteDesktop"
      data-testid="CustomAutoCompleteDesktop"
      ref={ref}
    >
      <div className={`position-relative ml-4 ${classes.inputContainer}`}>
        <TextField
          autoComplete="off"
          id="full-width-text-field-header"
          className={`SiteSearch__searchbox-desktop py-2 px-2rem a-searchTaggable ${classes.navInputText}`}
          placeholder={searchBarPlaceholderText}
          analytics={GLOBAL_SEARCH_QUERY_SUBMIT_INPUT}
          value={search}
          onFocus={() => {
            setIsFocused(true)
          }}
          onBlur={() => {
            setIsFocused(false)
          }}
          onChange={(e) => {
            const { value } = e.target

            setSearch(value)

            const supportAnalytics = ssrWindow._dl.supportDataLayer9_0
            if (!supportAnalytics) {
              ssrWindow._dl.supportDataLayer9_0 = {
                supportPageSearchResults: value,
              }
            } else {
              ssrWindow._dl.supportDataLayer9_0.supportPageSearchResults = value
            }

            if (value.length >= numCharacters || !value.length) {
              clearTimeout(timer)
              timer = setTimeout(() => refine(value), searchDelay)
            }
          }}
          onKeyDown={(e) => {
            if (e.keyCode === KEY_CODE_MAPPING.ENTER) {
              pushSearchSubmitData(search)
            }
            e.keyCode === KEY_CODE_MAPPING.TAB && onClose()
          }}
          fullWidth
          margin="normal"
          InputProps={{
            disableUnderline: true,
            startAdornment: (
              <InputAdornment position="start">
                <ReactSVG
                  wrapper="span"
                  className={classes.svg}
                  src={getContentUrl("/content/dam/wm/icons/search-icon.svg")}
                />
              </InputAdornment>
            ),
            "aria-describedby": "search-results",
            inputProps: {
              "aria-label": "Search box input",
            },
          }}
        />
        {isFocused && (
          <div
            className={`mx-2rem position-relative ${classes.inputUnderline}`}
          />
        )}
        <div
          className={`position-absolute bg-white w-100 maax-font-weight-regular ${classes.dropdown}`}
          style={{
            maxHeight: showDropdown ? 1000 : 0,
            borderWidth: showDropdown ? 1 : 0,
          }}
        >
          <div className="pb-3">
            <div>
              <p
                className="font-xs py-1 px-2rem"
                style={{
                  background: `#f4f4f4`,
                }}
                id="suggested-message"
                aria-live="assertive"
                aria-atomic="true"
              >
                {isPreSearch
                  ? suggestedText
                  : `${totalResults} ${resultsText || strings.RESULTS}`}
              </p>
              {totalResults === 0 && suggestionsCount === 0 && (
                <div className="mt-3 mx-3">
                  <div className="mx-3 wm-grey-light">
                    <div className="maax-font-weight-bold">
                      {error && error.name ? failureTitle : errorMessageTitle}
                    </div>
                    <div className="font-sm">
                      {error && error.name
                        ? failureDescription
                        : errorMessageDescription}
                    </div>
                  </div>
                </div>
              )}
              <>
                <Index indexName={queryIndex}>
                  <Configure
                    analyticsTags={["desktop-global-query"]}
                    hitsPerPage={numberQueries}
                    clickAnalytics
                  />
                  <Queries
                    indexName={queryIndex}
                    languageRoute={languageRoute}
                    isPreSearch={isPreSearch}
                  />
                </Index>
                {renderSuggested(!!isPreSearch)}
                {renderResults()}
                {!isPreSearch && !!(totalResults || suggestionsCount) && (
                  <div className="d-flex justify-content-center mb-3 mt-3">
                    <Link
                      className={`maax-font-weight-bold py-2 px-3 font-sm underline-focus ${classes.allResults}`}
                      href={`${languageRoute}/search?q=${encodeURIComponent(
                        search,
                      )}`}
                      isExternalLink={isExternalLink}
                    >
                      {seeAllResultsText}
                    </Link>
                  </div>
                )}
              </>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
})

GlobalDesktopSearch.displayName = `GlobalDesktopSearch`

// Note: React 19 no-longer supports propTypes. Leaving this for reference.
// GlobalDesktopSearch.propTypes = {
//   indices: PropTypes.array,
//   refine: PropTypes.func,
//   error: PropTypes.object,
//   errorMessageDescription: PropTypes.string.isRequired,
//   errorMessageTitle: PropTypes.string.isRequired,
//   failureDescription: PropTypes.string.isRequired,
//   failureTitle: PropTypes.string.isRequired,
//   language: PropTypes.string.isRequired,
//   numCharacters: PropTypes.number.isRequired,
//   numHighlightedResults: PropTypes.number.isRequired,
//   numResults: PropTypes.number.isRequired,
//   numberQueries: PropTypes.number.isRequired,
//   onClose: PropTypes.func.isRequired,
//   queryIndex: PropTypes.string.isRequired,
//   resultsText: PropTypes.string.isRequired,
//   search: PropTypes.string.isRequired,
//   searchBarPlaceholderText: PropTypes.string.isRequired,
//   seeAllResultsText: PropTypes.string.isRequired,
//   setSearch: PropTypes.func.isRequired,
//   strings: PropTypes.object.isRequired,
//   suggestedText: PropTypes.string.isRequired,
//   suggestionIndex: PropTypes.string.isRequired,
//   searchDelay: PropTypes.number.isRequired,
//   resultsIndex: PropTypes.string.isRequired,
//   filterType: PropTypes.string.isRequired,
// }

// Note: React 19 no-longer supports defaultProps. Leaving this for reference.
// GlobalDesktopSearch.defaultProps = {
//   indices: [],
//   refine: () => {},
//   error: null,
// }

const GlobalDesktopSearchContainer = (props) => {
  const stateResults = useSearchBox(props)
  const autoCompleteResults = useAutocomplete({ ...props, ...stateResults })

  return (
    <GlobalDesktopSearch
      {...props}
      {...stateResults}
      {...autoCompleteResults}
    />
  )
}

export default withLocalization(strings)(
  enhanceWithClickOutside(GlobalDesktopSearchContainer),
)
